import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "@shared/lib/http/axiosBaseQuery";
import { Table } from "@models/table";
import { ManufacturerModel } from "@models/manufacturer-model";

export enum SystemTableId {
  Manufacturers = 1,
  Warehouses = 2,
  Tags = 3,
  SortCodes = 4,
  ItemGroups = 5,
  Banks = 6,
}

export const systemTablesApi = createApi({
  reducerPath: "systemTablesApi",
  baseQuery: axiosBaseQuery({ baseUrl: "/api/" }),
  tagTypes: ["SystemTables", "SystemTablesOptions", "Models"],
  endpoints: (b) => ({
    getSystemTables: b.query<Table[], void>({
      query: () => ({
        url: "system-tables",
      }),
      providesTags: ["SystemTables"],
      keepUnusedDataFor: Infinity,
    }),

    getSystemTableOptions: b.query<Table[], number>({
      query: (tableNumber) => ({
        url: `system-tables-items/${tableNumber}`,
        params: { use_cache: 1 },
      }),
      providesTags: (_res, _err, tableNumber) => [{ type: "SystemTablesOptions", id: tableNumber }],
      keepUnusedDataFor: Infinity,
    }),

    getManufacturerModelsById: b.query<ManufacturerModel[], number>({
      query: (manufacturer_id) => ({
        url: "models",
        params: {
          sort: "name",
          "filter[manufacturer_id]": manufacturer_id,
        },
      }),
      providesTags: (_res, _err, manufacturer_id) => [{ type: "Models", id: manufacturer_id }],
      keepUnusedDataFor: Infinity,
    }),

    editSystemTable: b.mutation<Table, Table>({
      query: (systemTableToUpdate) => ({
        url: `system-tables/${systemTableToUpdate.id}`,
        method: "PATCH",
        data: systemTableToUpdate,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          systemTablesApi.util.updateQueryData("getSystemTables", undefined, (draft) => {
            const row = draft.find((t) => t.id === arg.id);
            if (row) Object.assign(row, arg);
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (_res, _err, arg) => [
        { type: "SystemTables" as const, id: arg.id },
        { type: "SystemTables" as const, id: "LIST" as const },
      ],
    }),

    editSystemTableOptionById: b.mutation<Table, { systemTableNumber: number; option: Table }>({
      query: ({ systemTableNumber, option }) => ({
        url: `system-tables-items/${systemTableNumber}/${option.id}`,
        method: "PATCH",
        data: option,
      }),
      async onQueryStarted({ systemTableNumber, option }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          systemTablesApi.util.updateQueryData("getSystemTableOptions", systemTableNumber, (draft) => {
            const row = draft.find((r) => r.id === option.id);
            if (row) Object.assign(row, option);
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (_res, _err, { systemTableNumber }) => [
        { type: "SystemTablesOptions", id: systemTableNumber },
      ],
    }),

    editModelOptionById: b.mutation<Table, ManufacturerModel>({
      query: (model) => ({
        url: `models/${model.id}`,
        method: "PATCH",
        data: model,
      }),
      async onQueryStarted(model, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          systemTablesApi.util.updateQueryData("getManufacturerModelsById", model.manufacturer_id, (draft) => {
            const row = draft.find((r) => r.id === model.id);
            if (row) Object.assign(row, model);
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (_res, _err, { manufacturer_id }) => [{ type: "Models", id: manufacturer_id }],
    }),

    addSystemTableOption: b.mutation<Table, { systemTableNumber: number; optionIndex: number }>({
      query: ({ systemTableNumber, optionIndex }) => ({
        url: `system-tables-items/${systemTableNumber}`,
        method: "POST",
        data: { index: optionIndex },
      }),
      async onQueryStarted({ systemTableNumber, optionIndex }, { dispatch, queryFulfilled }) {
        const temp: Table = { id: 99999, business_id: 99999, index: optionIndex, name: "" };
        const patch = dispatch(
          systemTablesApi.util.updateQueryData("getSystemTableOptions", systemTableNumber, (draft) => {
            draft.push(temp);
          }),
        );
        try {
          const { data: created } = await queryFulfilled;
          dispatch(
            systemTablesApi.util.updateQueryData("getSystemTableOptions", systemTableNumber, (draft) => {
              const i = draft.findIndex((x) => x.id === temp.id);
              if (i >= 0) draft[i] = created;
            }),
          );
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (_res, _err, { systemTableNumber }) => [
        { type: "SystemTablesOptions", id: systemTableNumber },
      ],
    }),

    addManufacturerModel: b.mutation<ManufacturerModel, { manufacturerId: number; modelIndex: number }>({
      query: ({ manufacturerId, modelIndex }) => ({
        url: "models",
        method: "POST",
        data: {
          manufacturer_id: manufacturerId,
          index: modelIndex,
        },
      }),
      async onQueryStarted({ manufacturerId, modelIndex }, { dispatch, queryFulfilled }) {
        const temp: ManufacturerModel = {
          id: 99999,
          business_id: 99999,
          manufacturer_id: manufacturerId,
          index: modelIndex,
          created_at: "",
          updated_at: "",
          name: "",
        };
        const patch = dispatch(
          systemTablesApi.util.updateQueryData("getManufacturerModelsById", manufacturerId, (draft) => {
            draft.push(temp);
          }),
        );
        try {
          const { data: created } = await queryFulfilled;
          dispatch(
            systemTablesApi.util.updateQueryData("getManufacturerModelsById", manufacturerId, (draft) => {
              const i = draft.findIndex((x) => x.id === temp.id);
              if (i >= 0) draft[i] = created;
            }),
          );
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (_res, _err, { manufacturerId }) => [{ type: "Models", id: manufacturerId }],
    }),
  }),
});

export const {
  useGetSystemTablesQuery,
  useGetSystemTableOptionsQuery,
  useGetManufacturerModelsByIdQuery,
  useLazyGetManufacturerModelsByIdQuery,
  useEditSystemTableMutation,
  useEditSystemTableOptionByIdMutation,
  useEditModelOptionByIdMutation,
  useAddSystemTableOptionMutation,
  useAddManufacturerModelMutation,
} = systemTablesApi;

export const useLazyGetTableOptionsByTableIndexQuery = () =>
  systemTablesApi.useLazyGetSystemTableOptionsQuery();

export const useGetManufacturersTableQuery = () =>
  systemTablesApi.useGetSystemTableOptionsQuery(SystemTableId.Manufacturers);

export const useGetWarehousesTableQuery = () =>
  systemTablesApi.useGetSystemTableOptionsQuery(SystemTableId.Warehouses);

export const useGetTagsTableQuery = () =>
  systemTablesApi.useGetSystemTableOptionsQuery(SystemTableId.Tags);

export const useGetSortCodesTableQuery = () =>
  systemTablesApi.useGetSystemTableOptionsQuery(SystemTableId.SortCodes);

export const useGetItemGroupsTableQuery = () =>
  systemTablesApi.useGetSystemTableOptionsQuery(SystemTableId.ItemGroups);

export const useGetBanksTableQuery = () =>
  systemTablesApi.useGetSystemTableOptionsQuery(SystemTableId.Banks);

export const useGetModelsByManufacturerIdQuery = (manufacturerId: number) =>
  systemTablesApi.useGetManufacturerModelsByIdQuery(manufacturerId);
